import jwtDecode from 'jwt-decode'

import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getActionsByBusinessUnit } from '@/utils/dictionaries/actionsDictionary/actions'
import { permissionsFromAD } from '@/utils/dictionaries/permissionsDictionary/permissionsDictionary'
import { getLocalUserRoles, getMicrosoftLoginUrl, isTestEnvironment } from '@/utils/envUtils'
import { isBrowser } from '@/utils/isBrowser'
import { isRouteValid } from '@/utils/routeUtils'
import { businessUnit } from '@/utils/themes/theme'
import { tickToDate } from '@/utils/tickToDate'
import { useConstructor } from '@/utils/useConstructor/useConstructor'

interface UserState {
  user: Record<string, unknown>
  logout: () => void
  permissions: number[]
  hasPermission: (action: string) => boolean
  isSessionValid: () => boolean
  setUserByToken: (token: string) => void
  login: () => void
  checkForToken: () => void
  hasMinimumPermissionsToLogin: (roles?: string[]) => boolean
}

export const UserStateContext = createContext<UserState>({} as UserState)

interface UserProviderProps {
  children: React.ReactNode
  init?: string | null
}

export const UserProvider = ({ children, init = null }: UserProviderProps) => {
  const [user, setUser] = useState<Record<string, unknown>>({})
  const [permissions, setPermissions] = useState<number[]>([])
  const navigate = useNavigate()
  const oauthHandledRef = useRef(false)

  const hasJwt = () => Boolean(isBrowser() && localStorage.getItem('backoffice-app-jwt'))

  const isSessionValid = () =>
    hasJwt()
      ? tickToDate((jwtDecode(localStorage.getItem('backoffice-app-jwt') as string) as { exp: number }).exp) >
        new Date()
      : false

  const getPermissionsByRoles = (roles?: string[]) =>
    roles
      ?.map((role) => permissionsFromAD(businessUnit(globalThis?.location?.hostname))[role])
      .filter((permission) => Boolean(permission)) as number[]

  const setPermissionsByRoles = (roles?: string[]) => setPermissions(getPermissionsByRoles(roles) ?? [])

  const hasMinimumPermissionsToLogin = (roles?: string[]) => Boolean(getPermissionsByRoles(roles)?.length)

  const setUserByToken = (token: string) => {
    const user = jwtDecode(token) as Record<string, unknown>
    const roles = Array.isArray(user?.roles) ? user?.roles?.map((role: string) => role.toLowerCase()) : []

    setUser({ ...user, roles })

    if (isTestEnvironment()) setPermissionsByRoles(getLocalUserRoles() || roles)
    else setPermissionsByRoles(roles)

    localStorage.setItem('backoffice-app-jwt', token)
  }

  const login = () => {
    const urlMicrosoftLogin = getMicrosoftLoginUrl()
    const redirectUri = encodeURIComponent(globalThis.location.href + 'oauth')
    const redirectUrl = `${urlMicrosoftLogin}&redirect_uri=${redirectUri}&scope=openid email profile&state=12345&nonce=678910`

    const anchor = document.createElement('a')
    anchor.href = redirectUrl
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  useConstructor(() => {
    if (init) setUserByToken(init)

    if (hasJwt()) {
      if (isSessionValid()) {
        setUserByToken(localStorage.getItem('backoffice-app-jwt') as string)
        if (isRouteValid(globalThis.location.pathname)) navigate(globalThis.location.pathname)
      } else {
        localStorage.removeItem('backoffice-app-jwt')
      }
    }
  })

  const checkForToken = useCallback(() => {
    if (!isBrowser() || oauthHandledRef.current) return
    if (!globalThis.location.pathname.includes('/oauth')) return

    oauthHandledRef.current = true

    const hash = globalThis.location.hash
    const token = hash ? new URLSearchParams(hash.slice(1)).get('id_token') : null

    if (token) {
      setUserByToken(token)
      navigate('/home')
    } else {
      navigate('/')
    }
  }, [navigate])

  const logout = () => {
    localStorage.removeItem('backoffice-app-jwt')
    setUser({})
    setPermissions([])
    navigate('/')
  }

  const hasPermission = (action: string) => {
    const actions = getActionsByBusinessUnit(businessUnit(globalThis?.location?.hostname))
    const roleAction = (actions[action] as number[]) || []
    return roleAction.some((permissionsToCheck) => permissions?.includes(permissionsToCheck))
  }

  const state: UserState = {
    user,
    logout,
    permissions,
    hasPermission,
    isSessionValid,
    setUserByToken,
    login,
    checkForToken,
    hasMinimumPermissionsToLogin
  }

  return <UserStateContext.Provider value={state}>{children}</UserStateContext.Provider>
}

export const useUserContext = () => useContext(UserStateContext)
